import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { FilterBar } from "@repo/ui/Filters/filterBar";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import {
  IconArrowUp,
  IconCircleCheck,
  IconPlus,
  IconSearch,
  IconTrendingUp,
} from "@tabler/icons-react";
import { useState, type FC, type FormEvent, type ReactNode } from "react";
import {
  Button as AriaButton,
  Input as AriaInput,
  Dialog,
  DialogTrigger,
  Form,
  VisuallyHidden,
} from "react-aria-components";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";
import {
  PageBody,
  PageLayout,
  PageLayoutHeight,
} from "../../components/layout/pageLayout.js";
import { useResearchAgentProps } from "../../features/copilot/copilotContext.js";
import { FilterResultSummary } from "../../features/filters/filterResultSummary.js";
import { useIsFeatureFlagEnabled } from "../../helpers/featureFlags/hooks.js";
import { Can } from "../../helpers/teammateAuthorizationContext.js";

const TargetIcon: FC = () => (
  <svg
    className="size-5 text-rose-500"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="5" />
    <circle cx="12" cy="12" r="1" />
  </svg>
);

interface ReportCardData {
  readonly title: string;
  readonly description: string;
  readonly insight?: string;
  readonly icon: ReactNode;
  readonly iconBg: string;
}

type ReportEntry = ReportCardData & {
  readonly templateId?: string;
};

const PerformanceReport: ReportEntry = {
  templateId: "template:performance",
  title: "Performance report",
  description: "Performance across all teams and all agents.",
  icon: <IconTrendingUp className="size-5 text-indigo-600" />,
  iconBg: "bg-indigo-50",
};

const ComplianceReport: ReportEntry = {
  templateId: "template:compliance",
  title: "Term life division - weekly compliance report",
  description: "Required disclosures and underwriting compliance.",
  icon: <IconCircleCheck className="size-5 text-blue-600" />,
  iconBg: "bg-blue-50",
};

const InsuranceReports: readonly ReportEntry[] = [
  ComplianceReport,
  {
    templateId: "template:insurance-upsell-outcome",
    title:
      "How coverage and term upsells affect inforce premium per opportunity",
    description:
      "Initial vs. recommended coverage, breakdown by team and carrier, and premium per opportunity effects.",
    icon: <IconTrendingUp className="size-5 text-emerald-600" />,
    insight:
      "Revenue per opportunity is 39% higher when agents recommend higher coverage or term",
    iconBg: "bg-emerald-50",
  },
];

/**
 * The report title for a `:reportId` URL segment, mirroring ReportDetailPage's
 * template switch (unknown ids fall back to the compliance report).
 */
export function resolveReportTitleByTemplateId(templateId: string): string {
  const report = [PerformanceReport, ...InsuranceReports].find(
    (entry) => entry.templateId === templateId,
  );
  return report?.title ?? ComplianceReport.title;
}

const AI_SUGGESTIONS: readonly string[] = [
  "Which agents drive the highest inforce premium?",
  "Why is compliance dropping this week?",
  "Show me coaching trends for new hires this quarter",
];

const reportCardClassName =
  "flex cursor-pointer items-start gap-3.5 rounded-xl bg-white p-5 shadow-xs ring-1 ring-black/5 transition-colors hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600";

const ReportCard: FC<{
  readonly report: ReportCardData;
  readonly to?: string;
}> = ({ report, to }) => {
  const body = (
    <>
      <div
        className={twMerge(
          "mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg",
          report.iconBg,
        )}
      >
        {report.icon}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-[15px] font-semibold text-foreground">
            {report.title}
          </span>
          <span className="text-[13px] leading-snug text-muted-foreground">
            {report.description}
          </span>
        </div>
        {report.insight ? (
          <div className="mt-3 inline-flex max-w-full min-w-0 items-start gap-2 self-start rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2">
            <div className="mt-1.5 size-[5px] shrink-0 rounded-full bg-indigo-600" />
            <span className="min-w-0 text-[13px] leading-snug font-medium text-indigo-950">
              {report.insight}
            </span>
          </div>
        ) : null}
      </div>
    </>
  );

  if (to) {
    return (
      <Link
        to={encodeURIComponent(to)}
        className={twMerge(reportCardClassName, "text-inherit no-underline")}
      >
        {body}
      </Link>
    );
  }

  return <div className={reportCardClassName}>{body}</div>;
};

const AiExplorer: FC = () => {
  const [query, setQuery] = useState("");
  const canSend = query.trim() !== "";
  const researchAgent = useResearchAgentProps();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSend || !researchAgent) return;

    const messageText = query.trim();
    setQuery("");
    researchAgent.onNewThreadSelect({ initialUserMessageText: messageText });
    researchAgent.onOpenChat();
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 rounded-xl bg-white p-5 shadow-xs ring-1 ring-black/5">
      <div className="flex shrink-0 items-center gap-2.5">
        <span className="text-sm font-semibold text-foreground">
          Research Agent
        </span>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
        {AI_SUGGESTIONS.map((suggestion) => (
          <AriaButton
            key={suggestion}
            onPress={() => {
              setQuery(suggestion);
            }}
            className="rounded-lg border border-border-light px-3 py-2.5 text-left text-[13px] text-gray-600 transition-colors hover:border-indigo-200 hover:bg-gray-50 hover:text-indigo-700"
          >
            {suggestion}
          </AriaButton>
        ))}
      </div>

      <Form
        className="flex shrink-0 items-center gap-2 rounded-2xl py-1 pr-1.5 ring-1 ring-border-strong ring-inset focus-within:ring-2 focus-within:ring-indigo-600"
        onSubmit={handleSubmit}
      >
        <AriaInput
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
          placeholder="Ask anything..."
          className="flex-auto border-none bg-transparent py-2 pr-0 pl-4 text-sm text-foreground placeholder-gray-400 focus:ring-0 focus:outline-hidden"
        />
        <AriaButton
          type="submit"
          isDisabled={!canSend}
          className="flex size-7 shrink-0 items-center justify-center rounded-full bg-indigo-800 text-white hover:bg-indigo-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-indigo-800 disabled:cursor-not-allowed disabled:bg-disabled"
        >
          <IconArrowUp className="size-5 shrink-0" />
          <VisuallyHidden>Send message</VisuallyHidden>
        </AriaButton>
      </Form>
    </div>
  );
};

export interface AllReportsPageProps {}

export const AllReportsPage: FC<AllReportsPageProps> = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const hasPerformanceReport = useIsFeatureFlagEnabled(
    FeatureFlagKey.PerformanceReport,
  );
  const hasInsuranceReports = useIsFeatureFlagEnabled(
    FeatureFlagKey.InsuranceReports,
  );
  const showResearchAgent = useIsFeatureFlagEnabled(
    FeatureFlagKey.ReportsResearchAgent,
  );
  const reports: readonly ReportEntry[] = [
    ...(hasPerformanceReport ? [PerformanceReport] : []),
    ...(hasInsuranceReports ? InsuranceReports : []),
  ];

  const query = searchQuery.trim().toLowerCase();
  const filteredReports = query
    ? reports.filter(
        (report) =>
          report.title.toLowerCase().includes(query) ||
          report.description.toLowerCase().includes(query),
      )
    : reports;

  return (
    <PageLayout height={PageLayoutHeight.Fills}>
      <PageHeader
        title="Reports"
        actions={
          <Can
            I={PermissionActions.UPDATE}
            a={PermissionSubjects.REPORTS}
            passThrough={true}
          >
            {(allowed) => (
              <>
                <DialogTrigger>
                  <Button
                    variant={ButtonVariant.Primary}
                    size={Size.SMALL}
                    icon={<IconPlus className="size-4 shrink-0" />}
                    isDisabled={!allowed || true}
                  >
                    Create a report
                  </Button>

                  <CenteredModal isDismissable size={Size.LARGE}>
                    <Dialog className="px-4 pt-5 pb-4 text-left sm:p-6">
                      {({ close }) => (
                        <>
                          <ModalHeader isCloseVisible>
                            Create a report
                          </ModalHeader>
                        </>
                      )}
                    </Dialog>
                  </CenteredModal>
                </DialogTrigger>
              </>
            )}
          </Can>
        }
        filterBar={
          <FilterBar
            leadingItems={
              <div className="w-56 shrink-0">
                <Input
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  placeholder="Search reports…"
                  leftIcon={<IconSearch className="size-4 text-gray-400" />}
                  maxLength={100}
                  size={Size.SMALL}
                />
              </div>
            }
            resultSummary={
              <FilterResultSummary
                count={filteredReports.length}
                denominatorCount={query ? reports.length : undefined}
              />
            }
            onReset={
              query
                ? () => {
                    setSearchQuery("");
                  }
                : undefined
            }
          />
        }
      />

      <PageBody fills>
        <div className="flex min-h-0 flex-1 items-start gap-5">
          <div className="flex min-w-0 flex-1 flex-col gap-3 self-start">
            {filteredReports.map(({ templateId, ...report }) => (
              <ReportCard
                key={templateId ?? report.title}
                report={report}
                to={templateId}
              />
            ))}
          </div>

          {showResearchAgent ? (
            <div className="sticky top-0 h-[450px] w-[340px] shrink-0 self-start">
              <AiExplorer />
            </div>
          ) : null}
        </div>
      </PageBody>
    </PageLayout>
  );
};
